import type { Coding, ServiceRequest } from '@medplum/fhirtypes'
import { ORDER_SYSTEM } from '#src/codeSystems.ts'

export const INTERNAL_ORDER_CODING = {
  system: ORDER_SYSTEM,
  code: 'internal-order',
  display: 'Internal Order',
}

export const ORDER_CODING = {
  system: ORDER_SYSTEM,
  code: 'order',
  display: 'Order',
}

export const ORDER_STATUS = {
  draft: 'Draft',
  'on-hold': 'Unsigned',
  active: 'In Progress',
  coordinated: 'Coordinated',
  effectuated: 'Effectuated',
  completed: 'Completed',
  revoked: 'Revoked',
  'entered-in-error': 'Entered in Error',
} as const

export type OrderStatus = keyof typeof ORDER_STATUS

export const ORDER_CATEGORY_CODE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/observation-category'

export const OrderCategory = {
  // Admin = 'admin',
  // Compliance = 'compliance',
  EscortServices: 'escortServices',
  HealthPlanManagement: 'healthPlanManagement',
  FacilitiesAndSafety: 'facilitiesAndSafety',
  // MarketingOutreachEnrollment = 'marketingOutreachEnrollment',
  HomeCare: 'homeCare',
  HomeHealth: 'homeHealth',
  Specialist: 'specialist',
  BehavioralHealth: 'behavioralHealth',
  Clinic: 'clinic',
  NutritionServices: 'nutritionServices',
  Rehabilitation: 'rehabilitation',
  // String values match the HEALTHCARE_SERVICE_CATEGORY codes for the same
  // services, so getTaskOwner can use the order's category directly to find
  // the right role in the rolesAndHealthcareServicePartials catalog.
  MassageTherapy: 'massage-therapy',
  SpeechTherapy: 'speech-therapy',
  RecreationalTherapy: 'recreationalTherapy',
  SocialWork: 'socialWork',
  PCACNA: 'pcaCna',
  // Quality = 'quality',
  // People = 'people',
  Transportation: 'transportation',
  MedicalRecords: 'medicalRecords',
  // Finance = 'finance',
  // GuestServicesRepresentative = 'guestServicesRepresentative',
  // AllServiceAreas = 'allServiceAreas',
  IDT: 'idt',
  // ParticipantExperience = 'participantExperience',
  DME: 'dme',
  Supplies: 'supplies',
  Lab: 'lab',
  Imaging: 'imaging',
  Cardiac: 'cardiac',
  Pulmonary: 'pulmonary',
  Misc: 'misc',
} as const
export type OrderCategory = (typeof OrderCategory)[keyof typeof OrderCategory]

export const ORDER_CATEGORY_DISPLAY_MAP: Record<OrderCategory, string> = {
  // [OrderCategory.Admin]: 'Admin',
  // [OrderCategory.Compliance]: 'Compliance',
  [OrderCategory.EscortServices]: 'Escort Services',
  [OrderCategory.HealthPlanManagement]: 'Health Plan Management',
  [OrderCategory.FacilitiesAndSafety]: 'Facilities & Safety',
  // [OrderCategory.MarketingOutreachEnrollment]: 'Marketing Outreach Enrollment',
  [OrderCategory.HomeCare]: 'Home Care (non-medical)',
  [OrderCategory.HomeHealth]: 'Home Care (medical)',
  [OrderCategory.Specialist]: 'Specialist',
  [OrderCategory.BehavioralHealth]: 'Behavioral Health',
  [OrderCategory.Clinic]: 'Clinic',
  [OrderCategory.NutritionServices]: 'Nutrition Services',
  [OrderCategory.Rehabilitation]: 'Rehabilitation',
  [OrderCategory.MassageTherapy]: 'Massage Therapy',
  [OrderCategory.SpeechTherapy]: 'Speech Therapy',
  [OrderCategory.RecreationalTherapy]: 'Recreational Therapy',
  [OrderCategory.SocialWork]: 'Social Work',
  [OrderCategory.PCACNA]: 'PCA/CNA',
  // [OrderCategory.Quality]: 'Quality',
  // [OrderCategory.People]: 'People',
  [OrderCategory.Transportation]: 'Transportation',
  [OrderCategory.MedicalRecords]: 'Medical Records',
  // [OrderCategory.Finance]: 'Finance',
  // [OrderCategory.GuestServicesRepresentative]: 'Guest Services Representative',
  // [OrderCategory.AllServiceAreas]: 'All Service Areas',
  [OrderCategory.IDT]: 'IDT',
  // [OrderCategory.ParticipantExperience]: 'Participant Experience',
  [OrderCategory.DME]: 'DME',
  [OrderCategory.Supplies]: 'Supplies',
  [OrderCategory.Lab]: 'Lab',
  [OrderCategory.Imaging]: 'Imaging',
  [OrderCategory.Cardiac]: 'Cardiac',
  [OrderCategory.Pulmonary]: 'Pulmonary',
  [OrderCategory.Misc]: 'Miscellaneous',
}

// Codes already stored on orders that are no longer in OrderCategory.
// Existing orders keep them. Do not write new orders with these codes.
export const RetiredOrderCategory = {
  Sleep: 'sleep',
} as const
export type RetiredOrderCategory =
  (typeof RetiredOrderCategory)[keyof typeof RetiredOrderCategory]

export const RETIRED_ORDER_CATEGORY_LABELS: Record<
  RetiredOrderCategory,
  string
> = {
  [RetiredOrderCategory.Sleep]: 'Sleep',
}

export const isRetiredOrderCategory = (
  code: string | null | undefined
): code is RetiredOrderCategory =>
  Object.values<string>(RetiredOrderCategory).includes(code ?? '')

export const isOrderCategory = (
  code: string | null | undefined
): code is OrderCategory =>
  Object.values<string>(OrderCategory).includes(code ?? '')

export const buildOrderCategoryCoding = (category: OrderCategory): Coding => ({
  system: ORDER_CATEGORY_CODE_SYSTEM,
  code: category,
  display: ORDER_CATEGORY_DISPLAY_MAP[category],
})

export const orderCategoryLabel = (
  category: OrderCategory | RetiredOrderCategory
): string =>
  isRetiredOrderCategory(category)
    ? RETIRED_ORDER_CATEGORY_LABELS[category]
    : ORDER_CATEGORY_DISPLAY_MAP[category]

export const ORDER_PRIORITY_TIMEFRAME = {
  'stat-1': { label: 'Same day', priority: 'stat' as const, days: 1 },
  'urgent-7': {
    label: 'Within 1 week',
    priority: 'urgent' as const,
    days: 7,
  },
  'asap-14': { label: 'Within 2 weeks', priority: 'asap' as const, days: 14 },
  'routine-30': {
    label: 'Within 4 weeks',
    priority: 'routine' as const,
    days: 30,
  },
  'routine-90': {
    label: 'Within 90 days',
    priority: 'routine' as const,
    days: 90,
  },
  'routine-180': {
    label: 'Within 180 days',
    priority: 'routine' as const,
    days: 180,
  },
  'routine-365': {
    label: 'Within 365 days',
    priority: 'routine' as const,
    days: 365,
  },
} as const

export type OrderPriorityTimeframe = keyof typeof ORDER_PRIORITY_TIMEFRAME

export const PRIORITY_TIMEFRAME_OPTIONS = Object.entries(
  ORDER_PRIORITY_TIMEFRAME
).map(([key, value]) => ({
  value: key as OrderPriorityTimeframe,
  ...value,
}))

export function orderPriorityGroupLabel(
  priority: OrderPriorityTimeframe | null
): string {
  if (!priority) {
    return 'No Priority'
  }
  const base = priority.split('-')[0]
  if (base === 'stat') {
    return 'STAT'
  }
  if (base === 'urgent') {
    return 'High'
  }
  if (base === 'asap') {
    return 'Medium'
  }
  return 'Low'
}

export type OrderPriority = ServiceRequest['priority']

export const ORDER_DETAILS = {
  description: {
    system: ORDER_SYSTEM,
    code: 'order-description',
    display: 'Order Description',
  },
  preferredVendor: {
    system: ORDER_SYSTEM,
    code: 'preferred-vendor',
    display: 'Preferred Vendor',
  },
} satisfies Record<string, Coding>
